import type { RichTranslateFn, TranslateFn } from '@newo/localization';
import { createContext, type PropsWithChildren, useContext } from 'react';
import { runtimeConfig } from '@/constants';
import { type CreatorRemoteProps, CreatorRenderMode } from '@/types/creator.mf';

const noop = () => {};
const noopTranslate: TranslateFn = (namespace, key) => {
	return `${namespace}.${key}`;
};
const noopRich: RichTranslateFn = (namespace, key) => {
	return [`${namespace}.${key}`];
};
interface HostContextValue {
	isMiniCreatorFeatureEnabled?: boolean;
	renderMode: CreatorRenderMode;
	enableMiniMode?: () => void;
	handleCloseCreator?: () => void;
	expandToPage?: () => void;
	partner_idn?: string | null;
	member_idn?: string | null;
	isProcessStarted?: boolean;
	localization: CreatorRemoteProps['localization'];
	handleAgentCreationSuccess?: () => void;
}

const defaultHostContext: HostContextValue = {
	enableMiniMode: noop,
	handleCloseCreator: noop,
	expandToPage: noop,
	isMiniCreatorFeatureEnabled: false,
	renderMode: CreatorRenderMode.Page,
	partner_idn: null,
	member_idn: null,
	isProcessStarted: false,
	localization: {
		translate: noopTranslate,
		rich: noopRich,
		language: 'en',
		onChangeLanguage: noop
	}
};

const HostContext = createContext<HostContextValue>(defaultHostContext);

type HostContextProviderProps = PropsWithChildren<{
	value: HostContextValue;
}>;

export function HostContextProvider({
	value,
	children
}: HostContextProviderProps) {
	return <HostContext.Provider value={value}>{children}</HostContext.Provider>;
}

export function DefaultHostProvider({ children }: PropsWithChildren) {
	return (
		<HostContext.Provider value={defaultHostContext}>
			{children}
		</HostContext.Provider>
	);
}

export function useHostContext() {
	const context = useContext(HostContext);

	if (runtimeConfig.isRemoteBuild && context === defaultHostContext) {
		throw new Error(
			'HostContext is missing. The host must provide HostContextProvider in micro-frontend mode.'
		);
	}

	const { renderMode } = context;

	return {
		...context,
		isMiniCreatorMode: renderMode === CreatorRenderMode.Mini,
		isOverlayMode: renderMode === CreatorRenderMode.Overlay,
		isPageMode: renderMode === CreatorRenderMode.Page
	};
}

export type { HostContextValue };
